import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreatePetRequest,
  UserMypageContractDetailRequest,
  UserMypageContractDetailResponse,
  TicketListResponse,
} from "~/types/common";

export const MYPAGE_QUERY_KEYS = {
  mypage: [
    "mypage",
    "main",
    "alarm-info",
    "contract-list",
    "contract-detail",
    "pet-create",
    "ticket-list",
    "guardian-list",
  ],
  contractDetail: ["user", "mypage", "contract-detail"],
} as const;

const getMypageMain = async () => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USER_MYPAGE.USER_MYPAGE_MAIN
    );
    return data.data || null;
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useMypageMain = () => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "main"],
    queryFn: () => getMypageMain(),
    select: (data) => data || null,
  });
};

/** 내 회원 고유코드 + 펫 목록(QR/오프라인 이용권 발급용) */
export interface MemberCodePet {
  petSeq: number;
  petName: string;
}
export interface MemberCodeResponse {
  memberCode: string;
  userName: string;
  pets: MemberCodePet[];
}
const getMemberCode = async (): Promise<MemberCodeResponse | null> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USER_MYPAGE.USER_MYPAGE_MEMBER_CODE
    );
    return data.data || null;
  } catch (error) {
    console.error("회원코드 조회 에러:", error);
    throw error;
  }
};
export const useMemberCode = () => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "member-code"],
    queryFn: () => getMemberCode(),
  });
};

const getMypageAlarmInfo = async () => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USER_MYPAGE.USER_MYPAGE_ALARM_INFO
    );
    return (
      data.data || {
        alarmEmailYn: "N",
        alarmMarketingYn: "N",
        alarmNoticeYn: "N",
        alarmSmsYn: "N",
      }
    );
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useMypageAlarmInfo = () => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "alarm-info"],
    queryFn: () => getMypageAlarmInfo(),
    select: (data) =>
      data || {
        alarmEmailYn: "N",
        alarmMarketingYn: "N",
        alarmNoticeYn: "N",
        alarmSmsYn: "N",
      },
  });
};

const getMypageContractList = async () => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USER_MYPAGE.USER_MYPAGE_CONTRACT_LIST
    );
    return data.data || [];
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useMypageContractList = () => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "contract-list"],
    queryFn: () => getMypageContractList(),
    select: (data) => data ?? [],
  });
};

// User Mypage Contract Detail 조회
const getUserMypageContractDetail = async (
  params: UserMypageContractDetailRequest
): Promise<UserMypageContractDetailResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USER_MYPAGE.USER_MYPAGE_CONTRACT_DETAIL,
      {
        params: {
          histContrSeq: params.histContrSeq,
        },
      }
    );
    return data;
  } catch (error) {
    console.error("User mypage contract detail API 호출 에러:", error);
    throw error;
  }
};

export const useUserMypageContractDetail = (
  params: UserMypageContractDetailRequest
) => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.contractDetail, params],
    queryFn: () => getUserMypageContractDetail(params),
    enabled: !!params.histContrSeq,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// 서버(UpdateUserAlarmSettingRequastDto)는 userSeq 를 받지 않는다.
// 대상 회원은 액세스 토큰에서 판별하므로 바디에 포함하지 않는다.
interface UpdateAlarmInfoRequest {
  alarmEmailYn: string;
  alarmMarketingYn: string;
  alarmNoticeYn: string;
  alarmSmsYn: string;
}

const updateMypageAlarmInfo = async (request: UpdateAlarmInfoRequest) => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.USER_MYPAGE.USER_MYPAGE_ALARM_INFO_UPDATE,
      request
    );
    return data.data;
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateMypageAlarmInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMypageAlarmInfo,
    onSuccess: () => {
      // 서버는 Void 를 반환하므로 응답으로 캐시를 덮어쓰지 않고 재조회한다.
      // (기존 구현은 useMypageAlarmInfo 와 다른 키에 써서 캐시가 갱신되지 않았다)
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "alarm-info"],
      });
    },
  });
};

// Pet 생성 API 함수
const createPet = async (
  request: CreatePetRequest
): Promise<{ message: string; statusCode: number }> => {
  try {
    // FormData 생성
    const formData = new FormData();

    // data 객체를 JSON 문자열로 변환하여 추가
    formData.append("param", JSON.stringify(request.data));

    // file이 있으면 추가 (Base64 문자열 또는 File 객체에 따라 처리)
    console.log("request.file", request.file);
    if (request.file) {
      if (request.file instanceof File) {
        formData.append("file", request.file);
      } else if (
        typeof request.file === "string" &&
        request.file.startsWith("data:")
      ) {
        // Base64 문자열을 Blob으로 변환
        const response = await fetch(request.file);
        const blob = await response.blob();
        formData.append("file", blob, "pet-image.jpg");
      }
    }

    const { data } = await privateHttp.post<{
      message: string;
      statusCode: number;
    }>(API_ENDPOINTS.USER_MYPAGE.PET_CREATE, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (error) {
    console.error("Pet 생성 API 호출 에러:", error);
    throw error;
  }
};

// Pet 생성 Mutation Hook
export const useCreatePet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPet,
    onSuccess: () => {
      // 성공 시 관련 쿼리들을 무효화하여 새로고침
      queryClient.invalidateQueries({
        queryKey: ["pet-list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "main"],
      });
    },
    onError: (error) => {
      console.error("Pet 생성 실패:", error);
    },
  });
};

// user info 조회 함수
const getUserInfo = async () => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.USER_MYPAGE.USER_INFO);
    return data.data || null;
  } catch (error) {
    console.error("유저 정보 조회 에러:", error);
    throw error;
  }
};

// user info useQuery hook
export const useUserInfo = () => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "user-info"],
    queryFn: getUserInfo,
    select: (data) => data || null,
  });
};

// 유저 정보 업데이트 API
interface UpdateUserInfoRequest {
  userName: string;
  nickname: string;
  phoneNumber: string;
}

const updateUserInfo = async (
  request: UpdateUserInfoRequest
): Promise<{ success: boolean; message: string }> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.USER_MYPAGE.UPDATE_INFO,
      request
    );
    return {
      success: true,
      message: data.message || "정보가 성공적으로 수정되었습니다.",
    };
  } catch (error) {
    console.error("유저 정보 수정 실패:", error);
    throw error;
  }
};

export const useUpdateUserInfo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUserInfo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "user-info"],
      });
    },
    onError: (error) => {
      console.error("유저 정보 수정 실패:", error);
    },
  });
};

// 반려동물 정보 조회 API
interface PetInfo {
  dtUpdate: string;
  delYn: string;
  dtRegist: string;
  petSeq: number;
  userSeq: number;
  petName: string;
  petRegNumber: string;
  cdPetBreed: string;
  cdPetGender: string;
  petBirthday: string;
  fileSeq: number;
  petWeight: number;
  petGradeSeq: number;
  mainPetYn: string;
  neuterYn: string;
  inoculation: string;
  personality: string;
  allergy: string;
  memo: string;
  fileUrl: string | null;
}

const getPetInfo = async (petSeq: number): Promise<PetInfo> => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.USER_MYPAGE.PET_INFO, {
      params: { petSeq },
    });
    return data.data;
  } catch (error) {
    console.error("반려동물 정보 조회 실패:", error);
    throw error;
  }
};

export const usePetInfo = (petSeq: number) => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "pet-info", petSeq],
    queryFn: () => getPetInfo(petSeq),
    enabled: !!petSeq,
    staleTime: 0, // 즉시 stale로 처리하여 항상 새로운 데이터 가져오기
    gcTime: 0, // 캐시 시간을 0으로 설정/ 윈도우 포커스 시에도 재호출
    retry: 1,
  });
};

// 펫 삭제 사유 목록 조회
const getPetDeleteReason = async () => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USER_MYPAGE.PET_DELETE_REASON
    );
    return data.data || [];
  } catch (error) {
    console.error("펫 삭제 사유 목록 조회 에러:", error);
    throw error;
  }
};

export const usePetDeleteReason = () => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "pet-delete-reason"],
    queryFn: () => getPetDeleteReason(),
    select: (data) => data || [],
  });
};

// 펫 삭제 요청 타입
interface PetDeleteRequest {
  petSeq: number;
  responseList: {
    activeYn: string;
    reasonName: string;
    cdPetDelete: string;
    etcContent: string | null;
  }[];
}

// 펫 삭제 응답 타입
interface PetDeleteResponse {
  success: boolean;
  message: string;
}

// 펫 삭제 API
const deletePet = async (
  request: PetDeleteRequest
): Promise<PetDeleteResponse> => {
  try {
    const { data } = await privateHttp.delete(
      API_ENDPOINTS.USER_MYPAGE.PET_DELETE,
      { data: request }
    );
    return data;
  } catch (error) {
    console.error("펫 삭제 API 호출 에러:", error);
    throw error;
  }
};

export const useDeletePet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePet,
    onSuccess: () => {
      // 성공 시 관련 쿼리들을 무효화하여 새로고침
      queryClient.invalidateQueries({
        queryKey: ["pet-list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "main"],
      });
    },
    onError: (error) => {
      console.error("펫 삭제 실패:", error);
    },
  });
};

// 이용권 리스트 조회 API
const getTicketList = async (
  isUseable: boolean = true,
  companySeq?: number
): Promise<TicketListResponse> => {
  try {
    const params: { isUseable: boolean; companySeq?: number } = {
      isUseable,
    };
    if (companySeq) {
      params.companySeq = companySeq;
    }
    const { data } = await privateHttp.get<TicketListResponse>(
      API_ENDPOINTS.USER_MYPAGE.TICKET_LIST,
      {
        params,
      }
    );
    return data;
  } catch (error) {
    console.error("이용권 리스트 조회 API 호출 에러:", error);
    throw error;
  }
};

// 이용권 리스트 조회 React Query Hook
export const useTicketList = (
  isUseable: boolean = true,
  companySeq?: number
) => {
  return useQuery({
    queryKey: [
      ...MYPAGE_QUERY_KEYS.mypage,
      "ticket-list",
      isUseable,
      companySeq,
    ],
    queryFn: () => getTicketList(isUseable, companySeq),
    select: (data) => data.data || [],
  });
};

// 이용권 상세 조회 API
export interface TicketDetailResponse {
  purchaseTicketSeq: number;
  productTicketSeq: number;
  userSeq: number;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: string;
  remainCount: number;
  totalCount: number;
  badge: string[];
  companyName: string;
  address: string;
  companySeq: number;
  purchaseDate: string;
  usableDate: string;
  expiredDate: string;
  cdProductType: string;
  /** 최초 이용일 방식: 최초 이용일로부터 사용 가능 일수 (null이면 기존 방식) */
  usageDurationDays?: number | null;
  /** 최초 이용(방문)일 — usageDurationDays가 있고 이 값이 null이면 아직 이용 시작 전 */
  dtFirstUse?: string | null;
  /** 환불 가능 여부 (백엔드 isRefundable, JSON에 refundable/isRefundable 둘 다 올 수 있음) */
  isRefundable?: boolean;
  refundable?: boolean;
}

const getTicketDetail = async (
  purchaseTicketSeq: number
): Promise<TicketDetailResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USER_MYPAGE.TICKET_DETAIL,
      {
        params: {
          purchaseTicketSeq,
        },
      }
    );
    return data.data;
  } catch (error) {
    console.error("이용권 상세 조회 API 호출 에러:", error);
    throw error;
  }
};

// 이용권 상세 조회 React Query Hook
export const useTicketDetail = (purchaseTicketSeq: number | null) => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "ticket-detail", purchaseTicketSeq],
    queryFn: () => getTicketDetail(purchaseTicketSeq!),
    enabled: !!purchaseTicketSeq,
    select: (data) => data || null,
  });
};

// 이용권 환불 API (PUT /user/mypage/ticket/refund)
export interface TicketRefundRequest {
  purchaseTicketSeq: number;
  reason: string;
}

const refundTicket = async (
  request: TicketRefundRequest
): Promise<void> => {
  try {
    await privateHttp.put(API_ENDPOINTS.USER_MYPAGE.TICKET_REFUND, request);
  } catch (error) {
    console.error("이용권 환불 API 호출 에러:", error);
    throw error;
  }
};

export const useRefundTicket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refundTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "ticket-detail"],
      });
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "ticket-list"],
      });
    },
  });
};

// 보호자 연동 목록 조회 API
export interface GuardianPet {
  dtUpdate: string;
  delYn: string;
  dtRegist: string;
  petSeq: number;
  userSeq: number;
  petName: string;
  petRegNumber: string;
  cdPetBreed: number;
  cdPetGender: "MALE" | "FEMALE";
  petBirthday: string;
  fileSeq: number;
  petWeight: number;
  petGradeSeq: number;
  mainPetYn: string;
  neuterYn: string;
  inoculation: string;
  personality: string;
  allergy: string;
  memo: string;
  fileUrl: string;
  petBreedName: string;
  hasContract: boolean;
}

export interface GuardianUser {
  guardianGroupMappingSeq: number;
  guardianGroupSeq: number;
  userSeq: number;
  cdGuardianType: string;
  name: string;
  nickname: string;
  email: string;
  mappingStatus: string;
  pets: GuardianPet[];
  currentUser: boolean;
}

interface GuardianListResponse {
  statusCode: number;
  data: {
    guardians: GuardianUser[];
    main: boolean;
  };
  message: string;
}

interface GuardianListData {
  guardians: GuardianUser[];
  main: boolean;
}

const getGuardianList = async (): Promise<GuardianListData> => {
  try {
    const { data } = await privateHttp.get<GuardianListResponse>(
      API_ENDPOINTS.USER_MYPAGE.GUARDIAN_LIST
    );
    return {
      guardians: data.data?.guardians || [],
      main: data.data?.main || false,
    };
  } catch (error) {
    console.error("보호자 연동 목록 조회 API 호출 에러:", error);
    throw error;
  }
};

// 보호자 연동 목록 조회 React Query Hook
export const useGuardianList = () => {
  return useQuery({
    queryKey: [...MYPAGE_QUERY_KEYS.mypage, "guardian-list"],
    queryFn: () => getGuardianList(),
    select: (data) => data || { guardians: [], main: false },
  });
};

// 보호자 연동 초대 요청 타입
interface GuardianInviteRequest {
  phoneNumber: string;
  nickname: string;
}

interface GuardianInviteResponse {
  statusCode: number;
  message: string;
}

// 보호자 연동 초대 API
const inviteGuardian = async (
  request: GuardianInviteRequest
): Promise<GuardianInviteResponse> => {
  try {
    const { data } = await privateHttp.post<GuardianInviteResponse>(
      API_ENDPOINTS.USER_MYPAGE.GUARDIAN_INVITE,
      request
    );
    return data;
  } catch (error) {
    console.error("보호자 연동 초대 API 호출 에러:", error);
    throw error;
  }
};

// 보호자 연동 초대 React Query Mutation Hook
export const useInviteGuardian = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteGuardian,
    onSuccess: () => {
      // 성공 시 보호자 목록 쿼리 무효화하여 새로고침
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "guardian-list"],
      });
    },
    onError: (error) => {
      console.error("보호자 연동 초대 실패:", error);
    },
  });
};

// 보호자 연동 해제 요청 타입
interface GuardianUnlinkRequest {
  phoneNumber: string;
  nickname: string;
}

interface GuardianUnlinkResponse {
  statusCode: number;
  message: string;
}

// 보호자 연동 해제 API
const unlinkGuardian = async (
  request: GuardianUnlinkRequest
): Promise<GuardianUnlinkResponse> => {
  try {
    const { data } = await privateHttp.delete<GuardianUnlinkResponse>(
      API_ENDPOINTS.USER_MYPAGE.GUARDIAN_LIST,
      { data: request }
    );
    return data;
  } catch (error) {
    console.error("보호자 연동 해제 API 호출 에러:", error);
    throw error;
  }
};

// 보호자 연동 해제 React Query Mutation Hook
export const useUnlinkGuardian = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unlinkGuardian,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...MYPAGE_QUERY_KEYS.mypage, "guardian-list"],
      });
    },
    onError: (error) => {
      console.error("보호자 연동 해제 실패:", error);
    },
  });
};
